import { useDispatch } from 'react-redux';
import { useEffect } from 'react';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import UIButton from 'UIComponents/button/UIButton';
import UILink from 'UIComponents/link/UILink';
import UIErrorMessage from 'UIComponents/error/UIErrorMessage';
import H4 from 'UIComponents/elements/headings/H4';
import UIAlert from 'UIComponents/alert/UIAlert';
import { FormattedIntlCurrency } from 'I18n/components/FormattedIntlCurrency';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import { NOT_FOUND, USER_NOT_FOUND, MEETING_PREVIOUSLY_CANCELLED, REBOOK_CONFLICT } from 'MeetingsLib/constants/errors';
import { getLongDisplayName } from 'MeetingsLib/utils/utils';
import { resetForPaidMeetingBookingFailure } from '../../redux/actions/FormActions';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const PaymentError = props => {
  const {
    meetingCreationFailed,
    onFailure,
    profile,
    meetingRescheduleError,
    NotFoundMarker,
    ErrorMarker,
    paidMeetingInfo,
    compact
  } = props;
  const dispatch = useDispatch();
  useEffect(() => {
    if (meetingCreationFailed) {
      onFailure();
    }
  }, [meetingCreationFailed, onFailure]);
  const handleResetApp = () => {
    dispatch(resetForPaidMeetingBookingFailure());
  };
  const renderHeader = message => {
    return /*#__PURE__*/_jsx(H4, {
      className: "m-bottom-2",
      children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: message
      })
    }, "header");
  };
  const renderPaidAlert = () => {
    const currencyAndAmount = /*#__PURE__*/_jsx(FormattedIntlCurrency, {
      value: paidMeetingInfo.total,
      currencyCode: paidMeetingInfo.currency
    });
    const paidMessage = /*#__PURE__*/_jsx(FormattedReactMessage, {
      message: "error.general.paidMeetingAlertTitle",
      options: {
        amount: currencyAndAmount
      }
    });
    return /*#__PURE__*/_jsx(UIAlert, {
      className: "m-x-auto",
      titleText: paidMessage,
      style: {
        width: compact ? '100%' : '40%'
      },
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "error.general.paidMeetingAlertMessage"
      })
    });
  };
  const renderDescription = (message, options = {}) => {
    return /*#__PURE__*/_jsx("p", {
      className: "error-description",
      children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: message,
        options: options
      })
    }, "description");
  };
  const renderEmail = () => {
    return /*#__PURE__*/_jsx("div", {
      className: "error-email",
      children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "error.emailLink_jsx",
        options: {
          fullName: getLongDisplayName(profile),
          emailAddress: profile.email,
          linkProps: {
            href: `mailto:${profile.email}`
          }
        },
        elements: {
          Link: UILink
        }
      })
    }, "email");
  };
  const renderRetryButton = desiredMessage => {
    const retryRebook = meetingRescheduleError === REBOOK_CONFLICT;
    const message = desiredMessage || 'error.button';
    return /*#__PURE__*/_jsx("div", {
      className: "error-button-container",
      children: /*#__PURE__*/_jsx(UIButton, {
        use: retryRebook ? 'primary' : 'tertiary',
        className: retryRebook ? '' : 'error-button',
        onClick: handleResetApp,
        children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
          message: message
        })
      })
    }, "retry");
  };
  const renderCreateErrorWithReload = () => {
    return /*#__PURE__*/_jsxs("div", {
      children: [renderPaidAlert(), /*#__PURE__*/_jsxs(UIErrorMessage, {
        title: renderHeader('error.general.header'),
        children: [renderDescription('error.general.retryDescription'), renderRetryButton(), renderEmail()]
      })]
    });
  };
  const renderRescheduleErrorWithReload = (descriptionMessage, buttonMessage) => {
    const desiredDescription = descriptionMessage || 'error.general.retryDescription';
    return /*#__PURE__*/_jsxs(UIErrorMessage, {
      title: renderHeader('error.general.header'),
      children: [renderDescription(desiredDescription), renderRetryButton(buttonMessage), renderEmail()]
    });
  };
  const renderRescheduleConflict = () => {
    const buttonMessage = 'error.buttonRebookConflict';
    const desiredDescription = 'error.general.rebookConflictError';
    return /*#__PURE__*/_jsxs(UIErrorMessage, {
      title: renderHeader('error.general.header'),
      illustration: "meetings",
      children: [renderDescription(desiredDescription), renderRetryButton(buttonMessage)]
    });
  };
  const renderInactiveState = () => {
    return /*#__PURE__*/_jsxs(UIErrorMessage, {
      type: "notFound",
      title: renderHeader('error.downgraded.header'),
      children: [renderDescription('error.downgraded.description'), renderEmail(), /*#__PURE__*/_jsx(NotFoundMarker, {})]
    });
  };
  const renderLinkNotFound = () => {
    return /*#__PURE__*/_jsxs(UIErrorMessage, {
      className: "link-not-found",
      type: "notFound",
      title: renderHeader('error.general.header'),
      children: [renderDescription('error.general.genericDescription'), /*#__PURE__*/_jsx(NotFoundMarker, {})]
    });
  };
  const renderError = () => {
    return /*#__PURE__*/_jsxs(UIErrorMessage, {
      children: [renderDescription('error.general.errorDescription'), /*#__PURE__*/_jsx(ErrorMarker, {})]
    });
  };
  const renderRescheduleFailure = () => {
    const rebookErrorMessage = 'error.general.rebookGeneralError';
    const rebookButtonMessage = 'error.buttonRebook';
    switch (meetingRescheduleError) {
      case MEETING_PREVIOUSLY_CANCELLED:
        return renderRescheduleErrorWithReload(rebookErrorMessage, rebookButtonMessage);
      case REBOOK_CONFLICT:
        return renderRescheduleConflict();
      default:
        return renderCreateErrorWithReload();
    }
  };
  const renderDifferentMessages = () => {
    const {
      bookInfoFailed,
      bookInfoError,
      meetingsUserFetchSuccess,
      meetingRescheduleFailed
    } = props;
    if (meetingRescheduleFailed) {
      return renderRescheduleFailure();
    }
    if (meetingCreationFailed) {
      return renderCreateErrorWithReload();
    }
    if (bookInfoFailed && (bookInfoError === NOT_FOUND || bookInfoError === USER_NOT_FOUND)) {
      if (meetingsUserFetchSuccess) {
        return renderInactiveState();
      }
      return renderLinkNotFound();
    }
    return renderError();
  };
  return /*#__PURE__*/_jsx("div", {
    className: "meetings-error text-center",
    children: renderDifferentMessages()
  });
};
export default PaymentError;